import { ReactNode } from 'react';
import { View, Text, Pressable, Image, ScrollView } from 'react-native';
import { useRouter, type Href } from 'expo-router';
import { Check, ChevronLeft, X } from 'lucide-react-native';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAuth } from '@/lib/supabase';
import { deferOnboarding } from '@/lib/onboarding/defer-onboarding';
import { persistOnboardingStep } from '@/lib/onboarding/persist-step';
import { STARTUP_ONBOARDING_STEP_IDS, STEP_LABELS, type OnboardingStep } from '@/lib/onboarding/steps';
import { STEP_ROUTES, getFlow, type InvitedMemberStep } from '@/lib/onboarding/machine';

import { LOGO_MARK } from '@/lib/logo';

/** Post-exit dashboard destination per role — mirrors the onboarded redirect in `src/app/index.tsx`. */
const DASHBOARD_ROUTE: Record<'startup' | 'investor', Href> = {
  startup: '/(startup)/startup-dashboard' as Href,
  investor: '/(investor)/investor-dashboard' as Href,
};

/** A step as rendered in the sidebar/progress UI: id + label + the route it links back to. */
export interface OnboardingStepDescriptor {
  id: string;
  label: string;
  path: string;
}

interface OnboardingLayoutProps {
  children: ReactNode;
  /**
   * Current step ID. Optional — Enhance-Profile screens (questionnaire-problem/
   * traction/market) reuse this layout but aren't part of the canonical
   * onboarding flow, so they render with no step highlighted/active.
   */
  currentStep?: string;
  /** All steps in the onboarding flow */
  steps: OnboardingStepDescriptor[];
  /** Title shown above the form */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** User type for context */
  userType: 'startup' | 'investor';
  /** Company/user name for personalization */
  entityName?: string;
  /** Show back button */
  showBack?: boolean;
  /** Custom back handler */
  onBack?: () => void;
}

/**
 * Unified onboarding layout that adapts to mobile and desktop
 *
 * Mobile: Full-screen with progress dots at top
 * Desktop: Sidebar wizard with step navigation
 */
export function OnboardingLayout({
  children,
  currentStep,
  steps,
  title,
  subtitle,
  userType,
  entityName,
  showBack = true,
  onBack,
}: OnboardingLayoutProps) {
  const colors = useSystemThemeColors();
  const router = useRouter();
  const { isDesktop } = useResponsive();
  const { user, refreshProfile } = useAuth();

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);
  const completedSteps = steps.slice(0, currentStepIndex);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (router.canGoBack()) {
      // Guard: the first onboarding step has nothing behind it (welcome was
      // removed), so a bare router.back() logs an unhandled GO_BACK.
      router.back();
    }
  };

  const handleExit = async () => {
    // Persist the resume pointer before exiting so a relaunch lands back on this
    // step. `onboarding_step` only tracks the startup/invitee flow (see
    // resolveResumeStep in @/lib/onboarding/steps) — investor onboarding and the
    // out-of-flow Enhance-Profile screens (currentStep undefined) don't write it.
    if (
      user?.id &&
      userType === 'startup' &&
      currentStep &&
      STARTUP_ONBOARDING_STEP_IDS.includes(currentStep as OnboardingStep)
    ) {
      await persistOnboardingStep(user.id, currentStep as OnboardingStep);
    }
    await deferOnboarding(user?.id, 'OnboardingLayout.handleExit');
    await refreshProfile();
    router.replace(DASHBOARD_ROUTE[userType]);
  };

  // Mobile layout
  if (!isWeb || !isDesktop) {
    return (
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Progress dots + Save & Exit */}
        <View className="flex-row items-center pt-4 px-6 gap-3">
          <View className="flex-row items-center flex-1 gap-2">
            {steps.map((step, index) => (
              <View
                key={step.id}
                className="h-1 flex-1 rounded-full"
                style={{
                  backgroundColor:
                    index <= currentStepIndex ? colors.primary[500] : colors.border.light,
                  maxWidth: 40,
                }}
              />
            ))}
          </View>
          <View style={{ height: 32, borderRadius: 16, overflow: 'hidden' }}>
            <Pressable
              onPress={handleExit}
              accessibilityRole="button"
              accessibilityLabel="Save and exit onboarding"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                height: 32,
                paddingHorizontal: 10,
                gap: 4,
              }}
            >
              <X size={14} color={colors.text.tertiary} />
              <Text
                numberOfLines={1}
                style={{ color: colors.text.tertiary }}
                className="text-xs font-medium"
              >
                Save & Exit
              </Text>
            </Pressable>
          </View>
        </View>
        {children}
      </View>
    );
  }

  // Desktop layout - wizard style
  return (
    <View className="flex-1 flex-row" style={{ backgroundColor: colors.surface.secondary }}>
      {/* Sidebar */}
      <View
        className="w-72 p-6 flex-col"
        style={{
          backgroundColor: colors.surface.primary,
          borderRightWidth: 1,
          borderRightColor: colors.border.light,
        }}
      >
        {/* Logo */}
        <View className="flex-row items-center gap-3 mb-8">
          <Image source={LOGO_MARK} style={{ width: 36, height: 36 }} resizeMode="contain" />
          <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
            Inverio
          </Text>
        </View>

        {/* Context */}
        {entityName && (
          <View className="mb-8 p-4 rounded-xl" style={{ backgroundColor: colors.primary[50] }}>
            <Text style={{ color: colors.primary[700] }} className="text-xs font-medium uppercase tracking-wide mb-1">
              {userType === 'startup' ? 'Setting up' : 'Your profile'}
            </Text>
            <Text style={{ color: colors.primary[800] }} className="text-base font-semibold">
              {entityName}
            </Text>
          </View>
        )}

        {/* Steps */}
        <View className="flex-1 gap-1">
          {steps.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isCurrent = step.id === currentStep;
            const isUpcoming = index > currentStepIndex;

            return (
              <AnimatedPressable
                key={step.id}
                onPress={() => {
                  if (isCompleted) {
                    router.push(step.path as Href);
                  }
                }}
                disabled={isUpcoming}
                className="flex-row items-center gap-3 p-3 rounded-lg"
                style={{
                  backgroundColor: isCurrent ? colors.primary[50] : 'transparent',
                  opacity: isUpcoming ? 0.5 : 1,
                }}
              >
                {/* Step indicator */}
                <View
                  className="w-8 h-8 rounded-full items-center justify-center"
                  style={{
                    backgroundColor: isCompleted
                      ? colors.primary[600]
                      : isCurrent
                      ? colors.primary[600]
                      : colors.surface.secondary,
                    borderWidth: isUpcoming ? 2 : 0,
                    borderColor: colors.border.light,
                  }}
                >
                  {isCompleted ? (
                    <Check size={16} color="white" />
                  ) : (
                    <Text
                      className="text-sm font-semibold"
                      style={{
                        color: isCurrent ? 'white' : colors.text.tertiary,
                      }}
                    >
                      {index + 1}
                    </Text>
                  )}
                </View>

                {/* Step label */}
                <Text
                  className="text-sm font-medium"
                  style={{
                    color: isCurrent
                      ? colors.primary[700]
                      : isCompleted
                      ? colors.text.primary
                      : colors.text.tertiary,
                  }}
                >
                  {step.label}
                </Text>
              </AnimatedPressable>
            );
          })}
        </View>

        {/* Exit button */}
        <AnimatedPressable
          onPress={handleExit}
          className="flex-row items-center gap-2 p-3 rounded-lg mt-4"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <X size={18} color={colors.text.tertiary} />
          <Text style={{ color: colors.text.tertiary }} className="text-sm">
            Save & Exit
          </Text>
        </AnimatedPressable>
      </View>

      {/* Main content */}
      <View className="flex-1">
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-8 py-4"
          style={{
            borderBottomWidth: 1,
            borderBottomColor: colors.border.light,
            backgroundColor: colors.surface.primary,
          }}
        >
          <View className="flex-row items-center gap-4">
            {showBack && currentStepIndex > 0 && (
              <AnimatedPressable
                onPress={handleBack}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <ChevronLeft size={20} color={colors.text.secondary} />
              </AnimatedPressable>
            )}
            <View>
              <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                {title}
              </Text>
              {subtitle && (
                <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
                  {subtitle}
                </Text>
              )}
            </View>
          </View>

          {/* Step indicator */}
          <Text style={{ color: colors.text.tertiary }} className="text-sm">
            Step {currentStepIndex + 1} of {steps.length}
          </Text>
        </View>

        {/* Content */}
        <ScrollView
          className="flex-1"
          contentContainerStyle={{
            padding: 32,
            maxWidth: 640,
            alignSelf: 'center',
            width: '100%',
          }}
          showsVerticalScrollIndicator={false}
        >
          {children}
        </ScrollView>
      </View>
    </View>
  );
}

// Predefined steps for startup onboarding.
// Flow: Your Profile → Company → Company Essentials → Data Input Choice → Complete
// The legacy `welcome` bookend was removed in the founder-flow reshape (Task 3);
// a fresh founder now enters directly at `personal`.
//
// Ids/labels are sourced from src/lib/onboarding/steps.ts, and the founder core
// routes from the canonical machine (`STEP_ROUTES.founder`, with 'data-source'
// aliased to the machine's 'activation'). Only the shared 'complete' result
// screen lives here directly.
const STARTUP_STEP_PATHS: Record<OnboardingStep, string> = {
  personal: STEP_ROUTES.founder.personal,
  company: STEP_ROUTES.founder.company,
  essentials: STEP_ROUTES.founder.essentials,
  'data-source': STEP_ROUTES.founder.activation,
  complete: '/(onboarding)/complete',
};

export const STARTUP_ONBOARDING_STEPS: OnboardingStepDescriptor[] = STARTUP_ONBOARDING_STEP_IDS.map((id) => ({
  id,
  label: STEP_LABELS[id],
  path: STARTUP_STEP_PATHS[id],
}));

// Invited-member progress descriptor (onboarding redesign Task 5). The
// invited-member flow is the canonical machine's 2-step `invite → personal`
// path — NOT the 4-step founder list — so an invitee on `startup-personal`
// shows "Step 2 of 2", never "Step 1 of 4". Sourced from the machine
// (`getFlow('invited_member')` + `STEP_ROUTES.invited_member`) so it can never
// drift from the ordering source of truth.
const INVITEE_STEP_LABELS: Record<InvitedMemberStep, string> = {
  invite: 'Accept Invite',
  personal: 'Your Profile',
};

export const INVITEE_ONBOARDING_STEPS: OnboardingStepDescriptor[] = getFlow('invited_member').map(
  (id) => ({
    id,
    label: INVITEE_STEP_LABELS[id as InvitedMemberStep],
    path: STEP_ROUTES.invited_member[id as InvitedMemberStep],
  }),
);

/**
 * Display steps for the founder progress indicator — excludes `complete`.
 * The completion/result screen is NOT a functional onboarding step (proposal
 * §Interaction: "the completion/result screen must not count as an additional
 * onboarding step"), so the "Step X of Y" count and the progress dots reflect
 * only the four real steps (personal → company → essentials → activation).
 */
export const STARTUP_ONBOARDING_DISPLAY_STEPS: OnboardingStepDescriptor[] =
  STARTUP_ONBOARDING_STEPS.filter((s) => s.id !== 'complete');

// Predefined steps for investor onboarding (redesign Task 6).
// Flow: Identity → Thesis → Complete. Routes for the two counted steps are
// sourced from the canonical machine (`STEP_ROUTES.investor`) so they never
// drift; 'complete' is the shared bookend (not a counted step — mirrors the
// startup list). LinkedIn verification is now an OPTIONAL sub-screen launched
// from the identity step, not its own onboarding step; portfolio capture moved
// post-onboarding to "Enhance profile".
export const INVESTOR_ONBOARDING_STEPS: OnboardingStepDescriptor[] = [
  { id: 'identity', label: 'Your Profile', path: STEP_ROUTES.investor.identity },
  { id: 'thesis', label: 'Investment Thesis', path: STEP_ROUTES.investor.thesis },
  { id: 'complete', label: 'Review & Complete', path: '/(onboarding)/complete' },
];
